import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { customersApi, type Customer } from '../api/customers'
import { dayOf } from '../api/projects'
import {
  DOMAIN_HOSTINGS, domainsApi, normalizeDomainName,
  type DomainDetail, type DomainHosting, type DomainRequest,
} from '../api/domains'
import {
  Button, Callout, Card, FormGrid, LoadingState, Page, PageHeader, Select, StatusPill, TextField, useConfirm, useSnackbar,
} from '../components/ui'
import { ExpiryCell } from './DomainsPage'
import './domains.css'

/**
 * Scheda di un dominio, anche nuovo (`/domains/new?customerId=`): cliente, note e tutti i campi. Un dominio che arriva
 * dalla piattaforma si può correggere qui; alla sync successiva i campi tecnici tornano quelli letti da iWebLab e dai
 * server, mentre note e cliente restano quelli scritti qui.
 */
interface Form {
  customerId: number | null
  name: string
  registrar: string
  registeredOn: string
  expiresOn: string
  providerSince: string
  providerDueOn: string
  autoRenew: '' | 'si' | 'no'
  providerStatus: string
  hosting: DomainHosting
  hostingServer: string
  dnsProvider: string
  notes: string
}

const EMPTY: Form = {
  customerId: null, name: '', registrar: '', registeredOn: '', expiresOn: '', providerSince: '', providerDueOn: '',
  autoRenew: '', providerStatus: '', hosting: 'nessuno', hostingServer: '', dnsProvider: '', notes: '',
}

/** Come il server: ragione sociale, altrimenti nome e cognome. */
function customerLabel(c: Customer): string {
  return c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.customerCode
}

function formOf(d: DomainDetail): Form {
  return {
    customerId: d.customerId, name: d.name, registrar: d.registrar ?? '', registeredOn: d.registeredOn ?? '',
    expiresOn: d.expiresOn ?? '', providerSince: d.providerSince ?? '', providerDueOn: d.providerDueOn ?? '',
    autoRenew: d.autoRenew === null ? '' : d.autoRenew ? 'si' : 'no', providerStatus: d.providerStatus ?? '',
    hosting: d.hosting, hostingServer: d.hostingServer ?? '', dnsProvider: d.dnsProvider ?? '', notes: d.notes ?? '',
  }
}

const today = () => {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

export function DomainDetailPage({ canDelete = false }: { canDelete?: boolean }) {
  const { id } = useParams()
  const isNew = !id || id === 'new'
  const domainId = isNew || !/^\d+$/.test(id) ? null : Number(id)
  const badId = !isNew && domainId === null
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const askConfirm = useConfirm()

  const [domain, setDomain] = useState<DomainDetail | null>(null)
  const [form, setForm] = useState<Form | null>(isNew ? { ...EMPTY, customerId: Number(params.get('customerId')) || null } : null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerHits, setCustomerHits] = useState<Customer[]>([])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (domainId === null) return
    let alive = true
    domainsApi.get(domainId).then(d => {
      if (!alive) return
      setDomain(d)
      setForm(formOf(d))
    }).catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Dominio non trovato.') })
    return () => { alive = false }
  }, [domainId])

  const customerId = form?.customerId ?? null
  useEffect(() => {
    if (customerId === null) { setCustomer(null); return }
    let alive = true
    customersApi.get(customerId).then(c => { if (alive) setCustomer(c) }).catch(() => { if (alive) setCustomer(null) })
    return () => { alive = false }
  }, [customerId])

  useEffect(() => {
    const q = customerSearch.trim()
    if (q.length < 2) { setCustomerHits([]); return }
    const t = setTimeout(() => {
      customersApi.list({ search: q, page: 1, size: 10 }).then(r => setCustomerHits(r.items)).catch(() => setCustomerHits([]))
    }, 300)
    return () => clearTimeout(t)
  }, [customerSearch])

  if (badId) return <Page><PageHeader title="Dominio" icon="language" /><Callout tone="error">Dominio non trovato.</Callout></Page>
  if (error && !form) return <Page><PageHeader title="Dominio" icon="language" /><Callout tone="error">{error}</Callout></Page>
  if (!form) return <Page><LoadingState /></Page>

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm(f => (f ? { ...f, [k]: v } : f))
  const orNull = (s: string) => (s.trim() ? s.trim() : null)

  async function save() {
    if (!form) return
    setError('')
    const name = normalizeDomainName(form.name)
    if (!name) { setError('Scrivi il dominio (es. esempio.it).'); return }
    const body: DomainRequest = {
      customerId: form.customerId, name, registrar: orNull(form.registrar),
      registeredOn: form.registeredOn || null, expiresOn: form.expiresOn || null,
      providerSince: form.providerSince || null, providerDueOn: form.providerDueOn || null,
      autoRenew: form.autoRenew === '' ? null : form.autoRenew === 'si', providerStatus: orNull(form.providerStatus),
      hosting: form.hosting, hostingServer: orNull(form.hostingServer), dnsProvider: orNull(form.dnsProvider), notes: orNull(form.notes),
    }
    setSaving(true)
    try {
      if (domainId === null) {
        const r = await domainsApi.create(body)
        snackbar.show({ message: 'Dominio creato' })
        navigate(`/domains/${r.id}`, { replace: true })
      } else {
        await domainsApi.update(domainId, body)
        snackbar.show({ message: 'Dominio salvato' })
        try {
          const fresh = await domainsApi.get(domainId)
          setDomain(fresh)
          setForm(formOf(fresh))
        } catch { /* salvato: la scheda si riallinea alla prossima apertura */ }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (domainId === null) return
    if (!await askConfirm({ title: 'Eliminare il dominio?', message: 'Il dominio sparisce dal CRM; se la piattaforma lo rimanda, torna alla prossima sincronizzazione.', confirmLabel: 'Elimina', destructive: true })) return
    try {
      await domainsApi.remove(domainId)
      snackbar.show({ message: 'Dominio eliminato' })
      navigate('/domains', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eliminazione non riuscita.')
    }
  }

  const serverHint = form.hosting === 'esterno' ? 'Fornitore (Aruba, Shopify...)' : form.hosting === 'iweblab' ? 'Server (smm1, smm2)' : 'Server'
  return (
    <Page>
      <PageHeader
        back={{ to: '/domains', label: 'Domini' }}
        title={isNew ? 'Nuovo dominio' : form.name || 'Dominio'}
        icon="language"
        actions={
          <>
            {!isNew && canDelete && <Button variant="text" icon="delete" onClick={() => void remove()}>Elimina</Button>}
            <Button variant="filled" icon="save" disabled={saving} onClick={() => void save()}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
          </>
        }
      />
      <div className="d-flex flex-column gap-3">
        {error && <Callout tone="error">{error}</Callout>}
        {domain && (
          <Callout tone="info" role="note">
            <StatusPill tone={domain.hostedByUs ? 'success' : 'neutral'}>{domain.hostedByUs ? 'Ospitato da noi' : 'Non ospitato da noi'}</StatusPill>{' '}
            <ExpiryCell expiresOn={domain.expiresOn} today={today()} />{' '}
            {domain.source === 'piattaforma'
              ? <>Aggiornato dalla piattaforma{domain.lastCheckedAt ? ` (ultimo controllo ${dayOf(domain.lastCheckedAt)})` : ''}: i campi tecnici si riallineano alla prossima sincronizzazione, note e cliente restano questi.</>
              : <>Inserito a mano il {dayOf(domain.createdAt)}.</>}
          </Callout>
        )}
        <Card variant="outlined">
          <FormGrid columns={2}>
            <div className="md-span-full">
              {form.customerId !== null ? (
                <div className="d-flex align-items-center gap-2">
                  <span>Cliente: <Link to={`/customers/${form.customerId}`}>{customer ? customerLabel(customer) : `n. ${form.customerId}`}</Link></span>
                  <Button variant="text" onClick={() => set('customerId', null)}>Cambia</Button>
                </div>
              ) : (
                <>
                  <TextField label="Cerca cliente" placeholder="Nome, ragione sociale o codice" value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    supportingText="Senza cliente il dominio resta da abbinare (lo vede solo chi vede tutti i clienti)." />
                  <div className="visually-hidden" aria-live="polite">
                    {customerSearch.trim().length >= 2 ? `${customerHits.length} clienti trovati` : ''}
                  </div>
                  {customerHits.length > 0 && (
                    <div className="d-flex flex-column mt-1" role="group" aria-label="Clienti trovati">
                      {customerHits.map(c => (
                        <Button key={c.id} variant="text" onClick={() => { set('customerId', c.id); setCustomerSearch(''); setCustomerHits([]) }}>
                          {customerLabel(c)} ({c.customerCode})
                        </Button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <TextField label="Dominio *" placeholder="esempio.it" maxLength={253} value={form.name} onChange={e => set('name', e.target.value)} />
            <TextField label="Registrar" maxLength={100} value={form.registrar} onChange={e => set('registrar', e.target.value)} />
            <TextField label="Registrato il" type="date" value={form.registeredOn} onChange={e => set('registeredOn', e.target.value)} />
            <TextField label="Scadenza nel registro" type="date" value={form.expiresOn} onChange={e => set('expiresOn', e.target.value)} />
            <TextField label="Presso iWebLab dal" type="date" value={form.providerSince} onChange={e => set('providerSince', e.target.value)} />
            <TextField label="Prossimo pagamento iWebLab" type="date" value={form.providerDueOn} onChange={e => set('providerDueOn', e.target.value)} />
            <Select label="Rinnovo automatico" value={form.autoRenew} onChange={e => set('autoRenew', e.target.value as Form['autoRenew'])}>
              <option value="">Non so</option>
              <option value="si">Sì</option>
              <option value="no">No</option>
            </Select>
            <TextField label="Stato presso iWebLab" placeholder="Attivo, Scaduto, Periodo di redenzione" maxLength={50}
              value={form.providerStatus} onChange={e => set('providerStatus', e.target.value)} />
            <Select label="Ospitato" value={form.hosting} onChange={e => set('hosting', e.target.value as DomainHosting)}>
              {DOMAIN_HOSTINGS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
            </Select>
            <TextField label={serverHint} maxLength={100} value={form.hostingServer} onChange={e => set('hostingServer', e.target.value)} />
            <TextField label="DNS gestiti da" placeholder="iWebLab, Aruba, Cloudflare" maxLength={100}
              value={form.dnsProvider} onChange={e => set('dnsProvider', e.target.value)} />
            <TextField className="md-span-full" label="Note" maxLength={2000} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </FormGrid>
        </Card>
      </div>
    </Page>
  )
}
